import { EventHandler, WithDelegateTarget } from "../../utils/event-handler";
import { throttle } from "../../utils";
import { EVENT_CLICK, EVENT_RESIZE } from "../../utils/events";

export class Tags {
    #SELECTOR_TAGS = '.tags';
    #SELECTOR_LIST = `${this.#SELECTOR_TAGS}__list`;
    #SELECTOR_ITEM = `${this.#SELECTOR_TAGS}__item`;
    #SELECTOR_ITEM_BTN = `${this.#SELECTOR_TAGS}__item--btn`;
    #SELECTOR_BTN = `${this.#SELECTOR_TAGS}__btn[aria-controls]`;

    #CLASS_NAME_LIST_OPEN = 'open';
    #CLASS_NAME_LIST_NOT_OVERFLOWING = 'not-overflowing';

    constructor() {
        this.#initElements();
        this.#initEvents();
    }

    #initElements(): void {
        document.querySelectorAll<HTMLElement>(`${this.#SELECTOR_LIST} ${this.#SELECTOR_BTN}`).forEach((btn) => {
            const list = this.#getList(btn);

            this.#setAriaExpanded(btn, this.#isOpen(list));

            this.#checkListOverflow(list);
        });
    }

    #initEvents(): void {
        EventHandler.on(document, EVENT_CLICK, `${this.#SELECTOR_LIST} ${this.#SELECTOR_BTN}`, (event: WithDelegateTarget<MouseEvent>) => {
            this.open(this.#getList(<HTMLElement>event.delegateTarget));
        });

        EventHandler.on(window, EVENT_RESIZE, throttle(() => {
            document.querySelectorAll<HTMLElement>(`${this.#SELECTOR_LIST} ${this.#SELECTOR_BTN}`).forEach((btn) => {
                this.#checkListOverflow(this.#getList(btn));
            });
        }, 100));
    }

    #getList(btn: HTMLElement): HTMLElement {
        return document.querySelector(`${this.#SELECTOR_LIST}#${btn.getAttribute('aria-controls')}`);
    }

    #getBtn(list: HTMLElement): HTMLElement {
        return document.querySelector(`${this.#SELECTOR_BTN}[aria-controls="${list.id}"]`);
    }

    #isOpen(list: HTMLElement): boolean {
        return list.classList.contains(this.#CLASS_NAME_LIST_OPEN);
    }

    #setAriaExpanded(btn: HTMLElement, isOpen: boolean): void {
        btn.setAttribute('aria-expanded', String(isOpen));
    }

    open(list: HTMLElement): void {
        if (this.#isOpen(list)) {
            return;
        }

        const btn = this.#getBtn(list);

        list.classList.add(this.#CLASS_NAME_LIST_OPEN);

        this.#setAriaExpanded(btn, true);
    }

    #checkListOverflow(list: HTMLElement): void {
        if (this.#isOpen(list)) {
            return;
        }

        const btnItem = list.querySelector<HTMLElement>(this.#SELECTOR_ITEM_BTN);
        btnItem.style.position = 'absolute';

        const { bottom: listBottom } = list.getBoundingClientRect();

        let item = Array.from(
            list.querySelectorAll<HTMLElement>(`${this.#SELECTOR_ITEM}:not(${this.#SELECTOR_ITEM_BTN})`)
        ).pop();

        if (item.getBoundingClientRect().bottom <= listBottom) {
            list.classList.add(this.#CLASS_NAME_LIST_NOT_OVERFLOWING);
            btnItem.style.position = '';
        } else {
            list.classList.remove(this.#CLASS_NAME_LIST_NOT_OVERFLOWING);

            do {
                item = <HTMLElement>item.previousElementSibling;

                if (item === btnItem) {
                    item = <HTMLElement>item.previousElementSibling;
                }
            } while (item.getBoundingClientRect().bottom > listBottom);

            list.insertBefore(btnItem, item.nextElementSibling);
            btnItem.style.position = '';

            if (btnItem.getBoundingClientRect().bottom > listBottom) {
                list.insertBefore(btnItem, item);
            }
        }
    }
}
